/* Sorenzia custom behaviour: size finder + delivery estimate.
   Uses document-level event delegation so it keeps working after Horizon
   re-renders (morphs) the product form when a variant changes. */
(() => {
  if (window.__szInit) return;
  window.__szInit = true;

  const CM_PER_IN = 2.54;
  const fmtLen = (cm, unit) =>
    unit === 'in' ? `${(cm / CM_PER_IN).toFixed(1)} in` : `${cm.toFixed(1)} cm`;

  /* ---------- helpers: find the size inputs of the product form ---------- */
  const scopeOf = (dialog) =>
    dialog.closest('.shopify-section, dialog.quick-add-modal, quick-add-dialog') || document;

  function sizeControls(scope) {
    const radios = [...scope.querySelectorAll('variant-picker input[type="radio"][data-option-name]')].filter((i) =>
      /size/i.test(i.dataset.optionName || '')
    );
    if (radios.length) return { type: 'radio', items: radios };
    const selects = [...scope.querySelectorAll('variant-picker select')].filter((s) =>
      /size/i.test(s.name || s.closest('.variant-option')?.querySelector('label')?.textContent || '')
    );
    if (selects.length) return { type: 'select', select: selects[0], items: [...selects[0].options] };
    return null;
  }

  const norm = (v) => String(v || '').toLowerCase().replace(/\s+/g, ' ').trim();

  function matchesRow(value, row) {
    const v = norm(value);
    const uk = norm(row.dataset.uk);
    const eu = norm(row.dataset.eu);
    const esc = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    if (eu && new RegExp(`(^|[^0-9.])eu\\s*${esc(eu)}([^0-9.]|$)`).test(v)) return true;
    if (uk && new RegExp(`(^|[^0-9.])uk\\s*${esc(uk)}([^0-9.]|$)`).test(v)) return true;
    if (v === uk || v === eu) return true;
    return false;
  }

  function controlForRow(ctrl, row) {
    if (!ctrl) return null;
    return ctrl.items.find((el) => matchesRow(el.value, row)) || null;
  }

  function selectSize(dialog, row) {
    const ctrl = sizeControls(scopeOf(dialog));
    const el = controlForRow(ctrl, row);
    if (!el) return false;
    if (ctrl.type === 'radio') {
      if (!el.checked) el.click();
    } else {
      ctrl.select.value = el.value;
      ctrl.select.dispatchEvent(new Event('change', { bubbles: true }));
    }
    return true;
  }

  /* ---------- dialog state ---------- */
  function setTab(dialog, tab) {
    dialog.querySelectorAll('[role="tab"][data-sz-tab]').forEach((b) => {
      const on = b.dataset.szTab === tab;
      b.classList.toggle('is-active', on);
      b.setAttribute('aria-selected', on);
    });
    dialog.querySelectorAll('[data-sz-pane]').forEach((p) => {
      const on = p.dataset.szPane === tab;
      p.classList.toggle('is-active', on);
      p.hidden = !on;
    });
  }

  function openGuide(id, trigger, tab = 'finder') {
    let dialog = id ? document.getElementById(`SzSizeGuide-${id}`) : null;
    if (!dialog) dialog = trigger?.closest('.shopify-section')?.querySelector('dialog.sz-sg') || document.querySelector('dialog.sz-sg');
    if (!dialog) return;
    setTab(dialog, tab);
    dialog._szTrigger = trigger;
    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');
    document.documentElement.classList.add('sz-scroll-lock');
    requestAnimationFrame(() => dialog.classList.add('is-open'));
  }

  function closeGuide(dialog) {
    if (!dialog || !dialog.open) return;
    dialog.classList.remove('is-open');
    const done = () => {
      if (dialog.open) dialog.close();
      document.documentElement.classList.remove('sz-scroll-lock');
      dialog._szTrigger?.focus?.({ preventScroll: true });
    };
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    reduce ? done() : setTimeout(done, 260);
  }

  /* The "Size guide" link next to the sizes opens the Size & fit section, where the chart lives. */
  function openSizeFit(trigger) {
    const scope = trigger.closest('.shopify-section') || document;
    const box = scope.querySelector('[data-sz-size-fit]') || document.querySelector('[data-sz-size-fit]');
    if (!box) return false;
    const details = box.closest('details');
    if (details && !details.open) {
      const summary = details.querySelector('summary');
      if (summary) summary.click();
      if (!details.open) details.open = true;
    }
    const target = details || box;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    setTimeout(() => target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' }), 60);
    return true;
  }

  function recommend(dialog, footCm) {
    const rows = [...dialog.querySelectorAll('[data-sz-row]')]
      .map((r) => ({ row: r, cm: parseFloat(r.dataset.cm) }))
      .filter((r) => !isNaN(r.cm))
      .sort((a, b) => a.cm - b.cm);
    if (!rows.length) return null;
    const max = rows[rows.length - 1];
    if (footCm > max.cm + 0.05) return { tooBig: true, max };
    const fit = rows.find((r) => r.cm + 0.05 >= footCm);
    return { fit, smallest: footCm < rows[0].cm - 0.5 };
  }

  function renderResult(dialog, footCm, unit) {
    const box = dialog.querySelector('[data-sz-result]');
    if (!box) return;
    const r = recommend(dialog, footCm);
    box.hidden = false;
    if (!r) {
      box.innerHTML = '<p>Sorry, no size chart is available for this style.</p>';
      return;
    }
    if (r.tooBig) {
      const d = r.max.row.dataset;
      box.innerHTML = `<p class="sz-sg__result-title">This style may be too small</p>
        <p>Your measurement (${fmtLen(footCm, unit)}) is longer than our largest size in this style, UK ${d.uk} (EU ${d.eu}, up to ${fmtLen(r.max.cm, unit)}).</p>`;
      return;
    }
    const d = r.fit.row.dataset;
    const ctrl = sizeControls(scopeOf(dialog));
    const el = controlForRow(ctrl, r.fit.row);
    const available = el && el.dataset.optionAvailable !== 'false';
    box.innerHTML = `<p class="sz-sg__result-kicker">Your recommended size</p>
      <p class="sz-sg__result-title">UK ${d.uk} <span>EU ${d.eu}</span></p>
      <p class="sz-sg__result-text">Fits a foot up to ${fmtLen(r.fit.cm, unit)}.${
        r.smallest ? ' Your measurement is below our smallest size, so this may feel roomy.' : ''
      }</p>
      ${
        el
          ? `<button type="button" class="button sz-sg__cta" data-sz-select-uk="${d.uk}" ${available ? '' : 'disabled'}>${
              available ? `Select UK ${d.uk}` : `UK ${d.uk} is sold out in this colour`
            }</button>`
          : ''
      }`;
  }

  /* ---------- events ---------- */
  document.addEventListener('click', (e) => {
    const t = e.target;
    if (!(t instanceof Element)) return;

    // "Find my size" in the Size & fit section, and any "#size-guide" link
    // (rich-text settings cannot carry data attributes) -> size finder dialog.
    const finder = t.closest('[data-sz-find-size], a[href="#size-guide"], a[href$="#size-guide"]');
    if (finder) {
      e.preventDefault();
      e.stopPropagation();
      openGuide(null, finder, 'finder');
      return;
    }
    // "Size guide" next to the sizes -> open Size & fit (chart); finder dialog as a fallback.
    const legendLink = t.closest('[data-sz-size-guide-open]');
    if (legendLink) {
      e.preventDefault();
      e.stopPropagation();
      if (!openSizeFit(legendLink)) openGuide(legendLink.dataset.szSizeGuideOpen, legendLink, 'finder');
      return;
    }
    const dialog = t.closest('dialog.sz-sg');
    if (!dialog) return;

    if (t === dialog || t.closest('[data-sz-size-guide-close]')) {
      closeGuide(dialog);
      return;
    }
    const tab = t.closest('[data-sz-tab]');
    if (tab) return setTab(dialog, tab.dataset.szTab);

    const fUnit = t.closest('[data-sz-finder-unit]');
    if (fUnit) {
      const form = fUnit.closest('form');
      form.dataset.unit = fUnit.dataset.szFinderUnit;
      form.querySelectorAll('[data-sz-finder-unit]').forEach((b) => {
        const on = b === fUnit;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-pressed', on);
      });
      const input = form.querySelector('[data-sz-foot]');
      input.placeholder = fUnit.dataset.szFinderUnit === 'in' ? 'e.g. 9.5' : 'e.g. 24.2';
      return;
    }

    const pick = t.closest('[data-sz-select-uk]');
    if (pick) {
      const row = [...dialog.querySelectorAll('[data-sz-row]')].find((r) => r.dataset.uk === pick.dataset.szSelectUk);
      if (row && selectSize(dialog, row)) closeGuide(dialog);
      return;
    }
  });

  document.addEventListener(
    'cancel',
    (e) => {
      if (e.target instanceof HTMLDialogElement && e.target.classList.contains('sz-sg')) {
        e.preventDefault();
        closeGuide(e.target);
      }
    },
    true
  );

  document.addEventListener('submit', (e) => {
    const form = e.target instanceof Element && e.target.closest('[data-sz-finder]');
    if (!form) return;
    e.preventDefault();
    const dialog = form.closest('dialog.sz-sg');
    const unit = form.dataset.unit || 'cm';
    const raw = parseFloat(String(form.querySelector('[data-sz-foot]').value).replace(',', '.'));
    const box = dialog.querySelector('[data-sz-result]');
    if (isNaN(raw) || raw <= 0) {
      box.hidden = false;
      box.innerHTML = '<p>Please enter your foot length.</p>';
      return;
    }
    const cm = unit === 'in' ? raw * CM_PER_IN : raw;
    if (cm < 15 || cm > 35) {
      box.hidden = false;
      box.innerHTML = `<p>That doesn't look like a foot length in ${unit}. Please check the unit and try again.</p>`;
      return;
    }
    renderResult(dialog, cm, unit);
  });

  /* ---------- delivery estimate ---------- */
  function addWorkingDays(start, n, holidays) {
    const d = new Date(start);
    let added = 0;
    while (added < n) {
      d.setDate(d.getDate() + 1);
      const day = d.getDay();
      const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      if (day !== 0 && day !== 6 && !holidays.has(iso)) added++;
    }
    return d;
  }

  function renderDelivery(root = document) {
    root.querySelectorAll('[data-sz-delivery]').forEach((el) => {
      const min = parseInt(el.dataset.min, 10);
      const max = parseInt(el.dataset.max, 10);
      if (!min || !max) return;
      const holidays = new Set((el.dataset.holidays || '').split(',').map((s) => s.trim()).filter(Boolean));
      const now = new Date();
      const a = addWorkingDays(now, Math.min(min, max), holidays);
      const b = addWorkingDays(now, Math.max(min, max), holidays);
      const f = new Intl.DateTimeFormat('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
      const target = el.querySelector('[data-sz-delivery-range]');
      const text = `${f.format(a)} – ${f.format(b)}`;
      if (target && target.textContent !== text) target.textContent = text;
    });
  }

  /* ---------- product description: presentation layer (2026-10-05) ----------
     The stored description is untouched (it also feeds Google Shopping). On the page we:
       - turn the "Key details" list into label/value rows,
       - drop the "Size & fit" and "What you receive" blocks (the size guide and the
         Size & fit accordion cover them),
       - drop the colour bullets (the swatches already show the colours).
     Progressive enhancement: if this never runs, the CSS still keeps the description tidy. */
  const DESC_SCOPE = '.product-details .accordion .details-content';
  const headingText = (el) => (el.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
  const isHeading = (el) => el instanceof Element && /^H[2-4]$/.test(el.tagName);
  const sectionOf = (h) => {
    const nodes = [];
    let n = h.nextSibling;
    while (n && !isHeading(n)) {
      nodes.push(n);
      n = n.nextSibling;
    }
    return nodes;
  };
  const COLOUR_BULLET = /^(?:colours?\s*:|(?:one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|thirteen|\d+)\s+colours?\b)/i;

  function enhanceDescription(root = document) {
    root.querySelectorAll(DESC_SCOPE).forEach((box) => {
      if (!box.querySelector('h3, h2, h4')) return;
      // Idempotent: safe to re-run after Horizon morphs the section on a variant change.
      box.querySelectorAll('h2, h3, h4').forEach((h) => {
        const t = headingText(h);
        if (/^size\s*&(?:amp;)?\s*fit$/.test(t) || t === 'size and fit' || t === 'what you receive') {
          sectionOf(h).forEach((n) => n.remove());
          h.remove();
          return;
        }
        if (t === 'key details') {
          const list = sectionOf(h).find((n) => n instanceof Element && n.tagName === 'UL');
          if (!list) return;
          list.classList.add('sz-spec');
          [...list.querySelectorAll('li')].forEach((li) => {
            const text = (li.textContent || '').trim();
            if (COLOUR_BULLET.test(text)) {
              li.remove();
              return;
            }
            const i = text.indexOf(':');
            if (i < 1 || li.querySelector('.sz-spec__k')) return;
            const k = text.slice(0, i).trim();
            const v = text.slice(i + 1).trim();
            li.textContent = '';
            const ke = document.createElement('span');
            ke.className = 'sz-spec__k';
            ke.textContent = k;
            const ve = document.createElement('span');
            ve.className = 'sz-spec__v';
            ve.textContent = v;
            li.append(ke, ve);
          });
          return;
        }
        // Benefit lists: remove the "N colours" bullet, keep the rest
        sectionOf(h).forEach((n) => {
          if (n instanceof Element && n.tagName === 'UL') {
            n.classList.add('sz-benefits');
            [...n.querySelectorAll('li')].forEach((li) => {
              if (COLOUR_BULLET.test((li.textContent || '').trim())) li.remove();
            });
          }
        });
      });
      box.querySelectorAll('ul').forEach((ul) => {
        if (!ul.children.length) ul.remove();
      });
    });
  }

  /* ---------- reviews: hide the Judge.me widget while a product has no reviews ----------
     An empty "Be the first to write a review" block is a negative signal for a new brand.
     The block reappears automatically once a genuine review exists (Judge.me renders it async). */
  function hideEmptyReviews() {
    document.querySelectorAll('#judgeme_product_reviews, .jdgm-review-widget').forEach((w) => {
      if (!w.classList.contains('jdgm--done-setup-widget')) return;
      const block = w.closest('.shopify-block') || w;
      const empty = /be the first to write a review/i.test(w.innerText || '');
      block.classList.toggle('sz-reviews-empty', empty);
    });
  }
  let reviewTries = 0;
  const reviewPoll = setInterval(() => {
    hideEmptyReviews();
    if (++reviewTries > 20) clearInterval(reviewPoll);
  }, 500);

  /* ---------- cart page: slim sticky "Total + Check out" bar on phones ---------- */
  function syncCartSticky() {
    const bar = document.querySelector('[data-sz-cart-sticky]');
    if (!bar) return;
    const ctas = document.querySelector('.cart-page .cart__ctas');
    if (!ctas || window.innerWidth >= 750) {
      bar.hidden = true;
      return;
    }
    const r = ctas.getBoundingClientRect();
    const visible = r.top < window.innerHeight && r.bottom > 0;
    bar.hidden = visible;
  }
  window.addEventListener('scroll', syncCartSticky, { passive: true });
  window.addEventListener('resize', syncCartSticky, { passive: true });

  let descTimer = 0;
  const scheduleDesc = () => {
    if (descTimer) return;
    descTimer = setTimeout(() => {
      descTimer = 0;
      enhanceDescription();
    }, 40);
  };

  const boot = () => {
    renderDelivery();
    enhanceDescription();
    syncCartSticky();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', (e) => {
    renderDelivery(e.target);
    enhanceDescription(e.target);
  });
  new MutationObserver((muts) => {
    let desc = false;
    let reviews = false;
    for (const m of muts) {
      const t = m.target instanceof Element ? m.target : m.target.parentElement;
      if (t && t.closest && t.closest('[data-sz-delivery]')) renderDelivery(document);
    }
    for (const m of muts)
      for (const n of m.addedNodes) {
        if (!(n instanceof Element)) continue;
        if (n.matches('[data-sz-delivery]') || n.querySelector('[data-sz-delivery]') || n.closest('[data-sz-delivery]')) renderDelivery(document);
        if (n.closest('.cart-page')) setTimeout(syncCartSticky, 50);
        if (n.matches(DESC_SCOPE) || n.querySelector(DESC_SCOPE) || n.closest(DESC_SCOPE)) desc = true;
        if (n.closest('#judgeme_product_reviews, .jdgm-review-widget')) reviews = true;
      }
    if (desc) scheduleDesc();
    if (reviews) hideEmptyReviews();
  }).observe(document.documentElement, { childList: true, subtree: true, characterData: true });
})();
